import cloneDeep from 'lodash/cloneDeep';
import set from 'lodash/set';
import { mapState } from 'vuex';
import dtwinApi from '@/services/apiService/digitalTwin';
import vdApi from '@/services/apiService/virtualDevice';
import debounce from 'lodash/debounce';
import get from 'lodash/get';
export default {
  name: 'TwinFilterEditor',
  props: {
    value: {
      type: Object,
      required: true
    },
    customParameters: {
      type: Object,
      required: true
    },
    dashboardId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      vdSearchString: '',
      parentTwinSearchString: '',
      isSearchingVds: false,
      isSearchingParentTwins: false,
      vdSearchItems: [],
      parentTwinSearchItems: []
    };
  },
  computed: {
    ...mapState('app', ['inpOutlined', 'inpFilled', 'image', 'color']),
    ...mapState('dashboards', ['dashboards']),
    dashboard() {
      return this.dashboards.find(dashboard => dashboard.id === this.dashboardId);
    },
    tags: {
      get() {
        const tags = get(this.value, 'tags', []);
        if (!Array.isArray(tags)) {
          return [];
        }
        return tags;
      },
      set(val) {
        this.$emit('input', {
          ...this.value,
          tags: val
        });
      }
    },
    selectionForParentTwinsInput() {
      const parentTwinIds = get(this.value, 'parentDtwinIds', []);
      return [...this.parentTwinSearchItems, ...this.dashboardParametersAsSelectItems].filter(item => parentTwinIds.includes(String(item.value))).filter((item, index, self) => self.findIndex(t => t.value === item.value) === index);
    },
    selectionForVdInput() {
      const vdIds = get(this.value, 'ids', []);
      return [...this.vdSearchItems, ...this.dashboardParametersAsSelectItems].filter(item => vdIds.includes(String(item.value))).filter((item, index, self) => self.findIndex(t => t.value === item.value) === index);
    },
    dashboardParametersAsSelectItems() {
      return Object.keys(this.customParameters).map(key => ({
        value: `{{${key}}}`,
        text: `{{${key}}}`
      }));
    }
  },
  watch: {
    dashboardId: {
      handler() {
        this.searchVds();
        this.searchParentTwins();
      },
      immediate: true
    },
    vdSearchString: {
      handler() {
        this.searchVds();
      },
      immediate: true
    },
    'value.ids': {
      handler() {
        this.searchVds();
      },
      immediate: true
    },
    parentTwinSearchString: {
      handler() {
        this.searchParentTwins();
      },
      immediate: true
    },
    'value.parentDtwinIds': {
      handler() {
        this.searchParentTwins();
      },
      immediate: true
    }
  },
  methods: {
    setByKey(key, val) {
      const clone = cloneDeep(this.value);
      if (key === 'tag') {
        val = val || undefined;
      }
      set(clone, key, val);
      this.$emit('input', clone);
    },
    searchParentTwins: debounce(function () {
      this.actuallySearchParentTwins();
    }, 500),
    searchVds: debounce(function () {
      this.actuallySearchVds();
    }, 500),
    async _fetchTwinsBySearchString(searchString) {
      const twins = [];
      const dashboardTenantId = get(this.dashboard, 'identifier.tenantId', undefined);
      if (!dashboardTenantId) {
        return twins;
      }
      const result = await dtwinApi.digitalTwinFetchPartialTree(this, undefined, searchString, undefined, ['bare'], dashboardTenantId);
      const recursiveAddTwinToTwins = twin => {
        twins.push({
          ...twin,
          children: undefined
        });
        if (Array.isArray(twin.children)) {
          twin.children.forEach(child => {
            recursiveAddTwinToTwins(child);
          });
        }
      };
      result.digitaltwins.forEach(twin => {
        recursiveAddTwinToTwins(twin);
      });
      return twins;
    },
    async actuallySearchParentTwins() {
      this.isSearchingParentTwins = true;
      try {
        const twins = [];
        const searchString = this.parentTwinSearchString;
        if ((searchString || '').length > 3) {
          twins.push(...(await this._fetchTwinsBySearchString(searchString)));
        } else {
          const dashboardTenantId = get(this.dashboard, 'identifier.tenantId');
          const dashboardParentTwinId = get(this.dashboard, 'meta.parentDtwinId');
          if (!dashboardTenantId || !dashboardParentTwinId) {
            return twins;
          }
          const result = await dtwinApi.digitalTwinFetchPartialTree(this, dashboardParentTwinId, undefined, undefined, ['bare'], dashboardTenantId);
          const addToTwins = twin => {
            twins.push(twin);
            if (Array.isArray(twin.children)) {
              twin.children.forEach(child => {
                addToTwins(child);
              });
            }
          };
          result.digitaltwins.forEach(twin => {
            addToTwins(twin);
          });
        }
        const searchItems = [...this.dashboardParametersAsSelectItems, ...twins.map(twin => {
          return {
            value: twin.id,
            text: twin.title
          };
        })];
        const previouslySelectedParentTwinIds = get(this.value, 'parentDtwinIds', []).map(Number).filter(val => !isNaN(val));
        if (previouslySelectedParentTwinIds.length > 0) {
          const {
            digitaltwins: previouslySelectedParentTwins
          } = await dtwinApi.digitalTwinsFetch(this, null, [], [{
            type: 'dtwinIds',
            ids: previouslySelectedParentTwinIds
          }]);
          searchItems.push(...previouslySelectedParentTwins.map(twin => ({
            value: twin.id,
            text: twin.title
          })));
        }
        this.parentTwinSearchItems = searchItems;
      } catch (e) {
        this.$snack(this.$t('Dashboards.components.virtualDeviceFilterEditor.snack.error.couldNotFetchParentTwins', {
          error: e.message
        }), 'error');
      } finally {
        this.isSearchingParentTwins = false;
      }
    },
    async actuallySearchVds() {
      this.isSearchingVds = true;
      try {
        let vds = [];
        const searchString = this.vdSearchString;
        const searchItemsParam = (searchString || '').trim() ? [{
          variable: 'quickSearch',
          search: searchString
        }] : [];
        const {
          virtualDevices
        } = await vdApi.virtualDeviceFetch(this, null, [], [{
          type: 'tenantIds',
          ids: [this.dashboard.identifier.tenantId]
        }], undefined, undefined, undefined, undefined, searchItemsParam);
        vds.push(...virtualDevices);
        const searchItems = [...this.dashboardParametersAsSelectItems, ...vds.map(vd => {
          return {
            value: vd.id,
            text: vd.title
          };
        })];
        const previouslySelectedVdIds = get(this.value, 'ids', []).map(Number).filter(val => !isNaN(val));
        if (previouslySelectedVdIds.length > 0) {
          const {
            virtualDevices: previouslySelectedVds
          } = await vdApi.virtualDeviceFetch(this, null, [], [{
            type: 'dtwinIds',
            ids: previouslySelectedVdIds
          }]);
          searchItems.push(...previouslySelectedVds.map(twin => ({
            value: twin.id,
            text: twin.title
          })));
        }
        this.vdSearchItems = searchItems;
      } catch (e) {
        this.$snack(this.$t('Dashboards.components.virtualDeviceFilterEditor.snack.error.couldNotFetchVds', {
          error: e.message
        }), 'error');
      } finally {
        this.isSearchingVds = false;
      }
    },
    setParentTwinSelection(val) {
      const clone = cloneDeep(val);
      this.$emit('input', {
        ...this.value,
        parentDtwinIds: clone.map(String)
      });
    },
    setVdSelection(val) {
      const clone = cloneDeep(val);
      this.$emit('input', {
        ...this.value,
        ids: clone.map(String)
      });
    }
  }
};